import React, { useEffect, useState } from "react";

// Brand palette used by every Landing subsection.
export const C = {
  green: "#006CFF",
  navy: "#0040A8",
  navyDeep: "#001A66",
  orange: "#0099FF",
  purple: "#9933FF",
  ink: "#0F172A",
  paper: "#F8FAFC",
  rule: "#E5E7EB",
};

/** Reveal — CSS-only fade-in keyed by `reveal-{delay}` utility classes. */
export function Reveal({ children, delay = 0, className = "", as: Tag = "div", ...rest }) {
  const dCls = delay ? `reveal-${delay}` : "";
  return (
    <Tag className={`reveal ${dCls} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * CountUp — numeric counter that animates from 0 to `value` on mount.
 * Supports prefix/suffix, decimals, and `format="comma"` for thousands separators.
 */
export function CountUp({ value, prefix = "", suffix = "", duration = 1600, decimals = 0, format }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setN(value * eased);
      if (progress >= 1) clearInterval(id);
    }, 32);
    return () => clearInterval(id);
  }, [value, duration]);
  const display = decimals
    ? n.toFixed(decimals)
    : format === "comma"
      ? Math.round(n).toLocaleString()
      : Math.round(n).toString();
  return <span>{prefix}{display}{suffix}</span>;
}

/** Stat — icon + animated value + label.  Shared by Impact + final CTA bands. */
export function Stat({ icon: Icon, value, label, color }) {
  const slug = String(label).replace(/\s+/g, "-").toLowerCase();
  return (
    <div className="group flex items-center gap-2.5 sm:gap-3 cursor-default min-w-0" data-testid={`stat-${slug}`}>
      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg grid place-items-center shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-md"
        style={{ background: `${color}15`, color }}>
        <Icon size={18} strokeWidth={2.2} />
      </div>
      <div className="leading-tight min-w-0">
        <div className="font-display font-extrabold text-lg sm:text-2xl tracking-tight transition-transform duration-300 group-hover:translate-x-0.5 whitespace-nowrap" style={{ color }}>{value}</div>
        <div className="text-[11px] sm:text-[12px] text-slate-500 leading-snug">{label}</div>
      </div>
    </div>
  );
}
